/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Boost domain - Player boost abilities and power-ups
 * boost.common.ts - Shared boost logic and utility functions
 */

import {
  BOOST_COOLDOWN,
  BOOST_DURATION,
  BOOST_MULTIPLIER,
  BOOST_SPEED_MULTIPLIER,
  BULLET_MINIMUM_SPEED,
  TICK_HZ,
} from "@game/config";
import type { BoostEffects, PlayerBoostData } from "./boost.types";

export function canPlayerBoost(
  player: PlayerBoostData,
  currentTime: number,
): boolean {
  return currentTime - player.lastBoostAt > BOOST_COOLDOWN * 1000;
}

/**
 * Tick-based variant: determines if player can boost at a given server tick.
 */
export function canPlayerBoostTick(
  player: PlayerBoostData,
  currentTick: number,
): boolean {
  const lastTick = player.lastBoostTick ?? -Infinity;
  const cooldownTicks = Math.ceil(BOOST_COOLDOWN * TICK_HZ);
  return currentTick - lastTick >= cooldownTicks && player.boostTimer <= 0;
}

export function isBoostActive(player: PlayerBoostData): boolean {
  return player.boostTimer > 0;
}

export function getRemainingBoostTime(player: PlayerBoostData): number {
  return Math.max(0, player.boostTimer);
}

export function getRemainingCooldownTime(
  player: PlayerBoostData,
  currentTime: number,
): number {
  const timeSinceBoost = currentTime - player.lastBoostAt;
  const cooldownMs = BOOST_COOLDOWN * 1000;
  return Math.max(0, (cooldownMs - timeSinceBoost) / 1000);
}

/**
 * Tick-based remaining cooldown (in ticks).
 */
export function getRemainingCooldownTicks(
  player: PlayerBoostData,
  currentTick: number,
): number {
  const lastTick = player.lastBoostTick ?? -Infinity;
  const cooldownTicks = Math.ceil(BOOST_COOLDOWN * TICK_HZ);
  return Math.max(0, cooldownTicks - (currentTick - lastTick));
}

export function calculateBoostEffects(player: PlayerBoostData): BoostEffects {
  const isActive = isBoostActive(player);

  return {
    thrustMultiplier: isActive ? BOOST_MULTIPLIER : 1.0,
    speedMultiplier: isActive ? BOOST_SPEED_MULTIPLIER : 1.0,
    remainingDuration: getRemainingBoostTime(player),
  };
}

export function applyBoostToThrust(
  baseThrust: number,
  player: PlayerBoostData,
): number {
  const effects = calculateBoostEffects(player);
  return baseThrust * effects.thrustMultiplier;
}

export function applyBoostToMaxSpeed(
  baseMaxSpeed: number,
  player: PlayerBoostData,
): number {
  const effects = calculateBoostEffects(player);
  return baseMaxSpeed * effects.speedMultiplier;
}

export function updateBoostTimer(
  player: PlayerBoostData,
  dt: number,
): PlayerBoostData {
  const result = { ...player };

  if (result.boostTimer > 0) {
    result.boostTimer = Math.max(0, result.boostTimer - dt);
  }

  return result;
}

export function enforceBulletMinimumSpeed(
  bulletVelX: number,
  bulletVelY: number,
  dirX: number,
  dirY: number,
): { x: number; y: number } {
  const bulletSpeedInDirection = bulletVelX * dirX + bulletVelY * dirY;

  if (bulletSpeedInDirection < BULLET_MINIMUM_SPEED) {
    const deficit = BULLET_MINIMUM_SPEED - bulletSpeedInDirection;
    console.log(
      `[BULLET] Enforced minimum speed: ${BULLET_MINIMUM_SPEED} (was: ${bulletSpeedInDirection.toFixed(0)})`,
    );
    return {
      x: bulletVelX + dirX * deficit,
      y: bulletVelY + dirY * deficit,
    };
  }

  return { x: bulletVelX, y: bulletVelY };
}

export function tryActivateBoost(
  player: PlayerBoostData,
  currentTime: number,
): { success: boolean; player: PlayerBoostData } {
  if (canPlayerBoost(player, currentTime) && player.boostTimer <= 0) {
    return {
      success: true,
      player: {
        ...player,
        lastBoostAt: currentTime,
        boostTimer: BOOST_DURATION,
      },
    };
  }

  return {
    success: false,
    player,
  };
}

/**
 * Tick-based boost activation: authoritative by server tick.
 */
export function tryActivateBoostTick(
  player: PlayerBoostData,
  currentTick: number,
): { success: boolean; player: PlayerBoostData } {
  if (canPlayerBoostTick(player, currentTick)) {
    return {
      success: true,
      player: {
        ...player,
        lastBoostTick: currentTick,
        boostTimer: BOOST_DURATION,
      },
    };
  }
  return { success: false, player };
}

export const BOOST_CONSTANTS = {
  BOOST_MULTIPLIER,
  BOOST_SPEED_MULTIPLIER,
  BOOST_DURATION,
  BOOST_COOLDOWN,
  BULLET_MINIMUM_SPEED,
} as const;
